import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "@pl-flight-recorder/web/store/hooks";
import MfaFeedbackService, {
  FeedbackFormResponse,
  SurveySchema,
  FeedbackFormInput,
} from "@pl-flight-recorder/web/services/MfaFeedbackService";
import {
  PulseTable,
  PulseSpinnerIcon,
  PulseButton,
  PulseButtonTheme,
  PulseButtonSizes,
  PulsePopupContainerSize,
} from "@pulselabs-ai/shared-fe-ui";
import { AddRoundedIcon } from "@pl-flight-recorder/web/components/icons";
import "./FeedbackContent.scss"; // Make sure this SCSS file exists
import CustomDialog from "@pl-flight-recorder/web/components/customDialog/CustomDialog";
import SurveyForm, {
  SurveyJson,
} from "@pl-flight-recorder/web/components/SurveyForm/SurveyForm";

const FeedbackContent: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAppSelector((state) => state.user);
  const token = user?.token;
  // const workspaceId = user?.defaultWorkspace?.id?.toString() || "";

  const [feedbackForms, setFeedbackForms] = useState<FeedbackFormResponse[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [isSurveyFormOpen, setIsSurveyFormOpen] = useState(false);
  const [surveyData, setSurveyData] = useState<SurveySchema | null>(null);
  const [editFormId, setEditFormId] = useState<string | number | null>(null);

  const fetchFeedbackForms = useCallback(async () => {
    if (!projectId || !token) {
      setError("Project ID or user token is missing.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const mfaFeedbackService = MfaFeedbackService.getInstance();
      const response = await mfaFeedbackService.getFeedbackFormsByProject(
        token,
        projectId,
      );
      setFeedbackForms(response);
      console.log("response", response);
    } catch (err) {
      console.error("Failed to fetch feedback forms:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load feedback forms.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchFeedbackForms();
  }, [fetchFeedbackForms]);

  const handleAddForm = () => {
    setSurveyData(null);
    setEditFormId(null);
    setIsSurveyFormOpen(true);
  };

  const handleCloseSurveyForm = () => {
    setIsSurveyFormOpen(false);
    setSurveyData(null);
    setEditFormId(null);
  };

  const handleEditForm = (form: FeedbackFormResponse) => {
    let parsedSchema: SurveySchema | null = null;
    try {
      parsedSchema =
        typeof form.schema === "string"
          ? JSON.parse(form.schema)
          : (form.schema ?? null);
    } catch (parseError) {
      console.error("Failed to parse survey schema for editing:", parseError);
      setError("Failed to load form data for editing.");
      return;
    }

    if (parsedSchema) {
      setSurveyData(parsedSchema);
      setEditFormId(form.surveyId);
      setIsSurveyFormOpen(true);
    } else {
      setError("Form data is missing or invalid.");
    }
  };

  const handleSaveSurvey = async (data: SurveyJson) => {
    console.log("Saving survey:", data);
    if (!projectId || !token) {
      setError("Cannot save form: Missing project/user info.");
      return;
    }

    try {
      const mfaFeedbackService = MfaFeedbackService.getInstance();

      if (editFormId) {
        const formData: FeedbackFormInput = {
          schema: data as SurveySchema,
          projectId: projectId,
          displayName: data.title || "Untitled Form",
          serviceMetadata: {
            source: "WEB",
          },
          surveyId: editFormId.toString(),
        };
        console.log(
          `Placeholder: Update form ${editFormId} with data:`,
          formData,
        );
        await mfaFeedbackService.updateFeedbackForm(
          token,
          user.id.toString(),
          // editFormId.toString(),
          formData,
        );
      } else {
        const formData: FeedbackFormInput = {
          schema: data as SurveySchema,
          projectId: projectId,
          displayName: data.title || "Untitled Form",
          serviceMetadata: {
            source: "WEB",
          },
        };
        await mfaFeedbackService.createFeedbackForm(
          token,
          user.id.toString(),
          formData,
        );
      }
      console.log("Form saved successfully");
      handleCloseSurveyForm();
      fetchFeedbackForms();
    } catch (saveError) {
      console.error("Failed to save feedback form:", saveError);
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save form.",
      );
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        field: "name",
        header: "Name",
        body: (item: FeedbackFormResponse) =>
          item.schema?.title || "Untitled Form",
      },
      {
        field: "status",
        header: "Status",
        body: (item: FeedbackFormResponse) => item.status,
      },
      {
        field: "description",
        header: "Description",
        body: (item: FeedbackFormResponse) => item.schema?.description || "-",
      },
      {
        field: "createdAt",
        header: "Created",
        body: (item: FeedbackFormResponse) =>
          new Date(item.createdAt).toLocaleDateString(),
      },
      {
        header: "Actions",
        body: (item: FeedbackFormResponse) => (
          <div className="table-actions">
            <PulseButton
              size={PulseButtonSizes.SMALL}
              theme={PulseButtonTheme.TERTIARY}
              onClick={() => handleEditForm(item)}
            >
              Edit
            </PulseButton>
          </div>
        ),
        style: { textAlign: "right" as const },
      },
    ],
    [],
  );

  return (
    <div className="feedback-content">
      <div className="header">
        {/* <h2>Feedback Forms</h2> */}
        <PulseButton
          startIcon={<AddRoundedIcon />}
          theme={PulseButtonTheme.SECONDARY}
          onClick={handleAddForm}
        >
          ADD NEW FORM
        </PulseButton>
      </div>

      {isLoading && (
        <div className="loading-spinner">
          <PulseSpinnerIcon />
        </div>
      )}
      {error && <div className="error-message">{error}</div>}

      {!isLoading && !error && (
        <PulseTable
          columnsDef={tableColumns}
          value={feedbackForms}
          rowHover={true}
          paginator={false}
          rows={feedbackForms.length || 1}
        />
      )}
      <CustomDialog
        key={editFormId || "new"}
        isOpen={isSurveyFormOpen}
        onClose={handleCloseSurveyForm}
        containerSize={PulsePopupContainerSize.CUSTOM}
        roundedCorners={true}
        isBackgroundClose={false}
        showCloseIcon={true}
      >
        <SurveyForm
          onClose={handleCloseSurveyForm}
          onSave={handleSaveSurvey}
          isEditing={!!editFormId}
          initialData={(surveyData as SurveyJson) || undefined}
        />
      </CustomDialog>
    </div>
  );
};

export default FeedbackContent;
